import {
  Box, Button, Popover, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FC, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';

import { defaultRestWindow, restWindowError, toJstIso } from './restWindow';

import type { TruckShiftRestKind, TruckShiftRestRequest } from 'src/hooks/useMutationTruckShiftRest';

type RestType = TruckShiftRestKind | 'CUSTOM';

/** 'YYYY-MM-DD' → 'M/D'。Date を作らずに切り出す（ブラウザのタイムゾーンで日がずれないように） */
export const restDateLabel = (date: string): string => {
  const [, month, day] = date.split('-');
  return month && day ? `${Number(month)}/${Number(day)}` : date;
};

type FormProps = {
  truckName: string;
  date: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (rest: TruckShiftRestRequest) => void;
};

const RestForm: FC<FormProps> = ({ truckName, date, submitting, onClose, onSubmit }) => {
  const [type, setType] = useState<RestType>('ALL');
  const initialWindow = defaultRestWindow(date);
  const [startAt, setStartAt] = useState<Date | null>(initialWindow?.start ?? null);
  const [endAt, setEndAt] = useState<Date | null>(initialWindow?.end ?? null);

  const isCustom = type === 'CUSTOM';
  /** 時間指定のときだけ出す。押してから初めて出すのではなく、不正なあいだは見えたままにする */
  const windowError = isCustom ? restWindowError(startAt, endAt) : null;

  /** 操作計測。粒度は V3Plans に合わせる（`button_name` は操作名、`label` は選んだ種別） */
  const track = (action: string, buttonName: string) => {
    ReactGA.event(action, {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL, button_name: buttonName, label: type,
    });
  };

  const handleSubmit = () => {
    track('click', '休み 設定する');
    if (!isCustom) {
      onSubmit({ date, kind: type });
      return;
    }
    // API も end_at <= start_at を 422 で弾くが、往復させると理由が出るまでに間が空く
    if (windowError || !startAt || !endAt) return;
    onSubmit({ start_at: toJstIso(startAt), end_at: toJstIso(endAt) });
  };

  return (
    <Box sx={{ '& > * + *': { mt: 1 } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
        {truckName}
        ・
        {restDateLabel(date)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>休みにする</Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={type}
        onChange={(_, value: RestType | null) => {
          if (!value) return;
          ReactGA.event('select', {
            screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL, button_name: '休み 種別', label: value,
          });
          setType(value);
        }}
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="ALL">終日</ToggleButton>
        <ToggleButton value="AM">午前</ToggleButton>
        <ToggleButton value="PM">午後</ToggleButton>
        <ToggleButton value="CUSTOM">時間指定</ToggleButton>
      </ToggleButtonGroup>

      {/* 日付ごと選ばせる（サンプル MultiDayBoard.tsx:239-256 と同じ format / ampm）。
          時刻だけだと、夜跨ぎの休みが翌日まで続くことが画面のどこにも出ない */}
      {isCustom && (
        <Stack direction="row" spacing={1} alignItems="center">
          <DateTimePicker
            label="開始"
            value={startAt}
            onChange={setStartAt}
            format="M/d HH:mm"
            ampm={false}
            slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
          />
          <DateTimePicker
            label="終了"
            value={endAt}
            onChange={setEndAt}
            format="M/d HH:mm"
            ampm={false}
            slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
          />
        </Stack>
      )}

      {windowError && (
        <Typography sx={{ fontSize: 11, color: 'error.main' }}>{windowError}</Typography>
      )}

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button size="small" variant="text" onClick={onClose}>キャンセル</Button>
        {/*
          Why not 窓が不正なあいだ「設定する」を disabled にする: disabled のボタンは
          フォーカスが当たらず読み上げからも外れるので、なぜ設定できないのかに気づけない。
          押しても送らず、理由はボタンの上に出したままにする。
        */}
        <Button size="small" variant="contained" disabled={submitting} onClick={handleSubmit}>
          設定する
        </Button>
      </Stack>
    </Box>
  );
};

type Props = FormProps & { open: boolean; anchorEl: HTMLElement | null };

/**
 * 案件が載っていないセルを押したときの「休みにする」。
 * 終日 / 午前 / 午後 / 時間指定 を選んで勤務から時間帯を引く（午前・午後の境界は API が会社設定から決める）。
 *
 * Why not 稼働表の `CellEditorPopover`: あれは勤務の時刻を直接編集する道具で、分割できず
 * 案件の時間も踏み潰せる。長距離配車では「休みにする」という 1 操作だけを出す。
 */
export const LongHaulRestPopover: FC<Props> = ({ open, anchorEl, ...formProps }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={formProps.onClose}
    anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    slotProps={{ paper: { sx: { mt: 1, p: 1.5, minWidth: 260 } } }}
  >
    {/* 開くたびに選択を初期状態へ戻す（前回の「午前」が残ったまま別の日を押さないように） */}
    {open && <RestForm key={`${formProps.truckName}::${formProps.date}`} {...formProps} />}
  </Popover>
);
